<template>
  <transition
    name="zm-zoom-in-top"
    @after-enter="handleEnter"
    @after-leave="handleLeave"
  >
    <div
      class="zm-table-filter"
      v-if="multiple"
      v-clickoutside="handleOutsideClick"
      v-show="showPopper">
      <div class="zm-table-filter__content">
        <zm-scrollbar wrap-class="zm-table-filter__wrap">
          <zm-checkbox-group class="zm-table-filter__checkbox-group" v-model="filteredValue">
            <zm-checkbox
              v-for="filter in filters"
              :key="filter.value"
              :label="filter.value">{{ filter.text }}
            </zm-checkbox>
          </zm-checkbox-group>
        </zm-scrollbar>
      </div>
      <div class="zm-table-filter__bottom">
        <zm-button
          type="primary"
          @click="handleConfirm"
          :class="{ 'is-disabled': filteredValue.length === 0 }"
          :disabled="filteredValue.length === 0">{{ t('table.confirmFilter') }}
        </zm-button>
        <zm-button @click="handleReset">{{ t('table.resetFilter') }}</zm-button>
      </div>
    </div>
    <div
      class="zm-table-filter"
      v-else
      v-clickoutside="handleOutsideClick"
      v-show="showPopper">
      <ul class="zm-table-filter__list">
        <li class="zm-table-filter__list-item"
            :class="{ 'is-active': filterValue === undefined || filterValue === null }"
            @click="handleSelect(null)">{{ t('table.clearFilter') }}
        </li>
        <li class="zm-table-filter__list-item"
            v-for="filter in filters"
            :label="filter.value"
            :key="filter.value"
            :class="{ 'is-active': isActive(filter) }"
            @click="handleSelect(filter.value)">{{ filter.text }}
        </li>
      </ul>
    </div>
  </transition>
</template>

<script type="text/babel">
  import Popper from '../../utils/vue-popper';
  // import { PopupManager } from '../../utils/popup';
  import Locale from '../../mixins/locale';
  import Clickoutside from '../../utils/clickoutside';
  import Dropdown from './dropdown';
  import ZmCheckbox from '../checkbox';
  import ZmCheckboxGroup from '../checkbox-group';
  import ZmScrollbar from '../scrollbar';
  import ZmButton from '../button';
  import AriaDialog from '../../utils/aria-dialog';
  import AriaUtils from '../../utils/aria-utils';

  export default {
    name: 'ZmTableFilterPanel',

    mixins: [Popper, Locale],

    directives: {
      Clickoutside
    },

    components: {
      ZmCheckbox,
      ZmCheckboxGroup,
      ZmScrollbar,
      ZmButton
    },

    props: {
      placement: {
        type: String,
        default: 'bottom-end'
      }
    },

    methods: {
      isActive(filter) {
        return filter.value === this.filterValue;
      },

      handleOutsideClick() {
        setTimeout(() => {
          this.showPopper = false;
        }, 16);
      },

      handleConfirm() {
        this.confirmFilter(this.filteredValue);
        this.handleOutsideClick();
      },

      handleReset() {
        this.filteredValue = [];
        this.confirmFilter(this.filteredValue);
        this.handleOutsideClick();
      },

      handleSelect(filterValue) {
        this.filterValue = filterValue;

        if ((typeof filterValue !== 'undefined') && (filterValue !== null)) {
          this.confirmFilter(this.filteredValue);
        } else {
          this.confirmFilter([]);
        }

        this.handleOutsideClick();
      },

      confirmFilter(filteredValue) {
        this.table.store.commit('filterChange', {
          column: this.column,
          values: filteredValue
        });
        this.table.store.updateAllSelected();
      },

      handleEnter() {
        document.body.addEventListener('keydown', this.handleKeydown);
        const focusAfterClosed = document.activeElement;
        this.ariaDialog = new AriaDialog(this.$el, focusAfterClosed);
      },

      handleLeave() {
        document.body.removeEventListener('keydown', this.handleKeydown);
        this.ariaDialog && this.ariaDialog.closeDialog();
      },

      handleKeydown(event) {
        const keyCode = event.keyCode;
        const keys = AriaUtils.keys;
        switch (keyCode) {
          case keys.esc:
            this.handleOutsideClick();
            break;
        }
      }
    },

    data() {
      return {
        table: null,
        cell: null,
        column: null
      };
    },

    computed: {
      filters() {
        return this.column && this.column.filters;
      },

      filterValue: {
        get() {
          return (this.column.filteredValue || [])[0];
        },
        set(value) {
          if (this.filteredValue) {
            if ((typeof value !== 'undefined') && (value !== null)) {
              this.filteredValue.splice(0, 1, value);
            } else {
              this.filteredValue.splice(0, 1);
            }
          }
        }
      },

      filteredValue: {
        get() {
          if (this.column) {
            return this.column.filteredValue || [];
          }
          return [];
        },
        set(value) {
          if (this.column) {
            this.column.filteredValue = value;
          }
        }
      },

      multiple() {
        if (this.column) {
          return this.column.filterMultiple;
        }
        return true;
      }
    },

    mounted() {
      this.popperElm = this.$el;
      this.referenceElm = this.cell;
      this.table.bodyWrapper.addEventListener('scroll', () => {
        this.updatePopper();
      });

      this.$watch('showPopper', (value) => {
        if (this.column) this.column.filterOpened = value;
        if (value) {
          Dropdown.open(this);
        } else {
          Dropdown.close(this);
        }
      });
    }
  };
</script>


<style lang="scss">
  @import "../../scss/mixins/mixins";
  @import "../../scss/common/var";

  @include b(table-filter) {
    border: solid 1px $--border-color-lighter;
    border-radius: 2px;
    background-color: $--color-white;
    box-shadow: $--dropdown-menu-box-shadow;
    box-sizing: border-box;
    margin: 2px 0;

    /** used for dropdown mode */
    @include e(list) {
      padding: 5px 0;
      margin: 0;
      list-style: none;
      min-width: 100px;
    }

    @include e(list-item) {
      line-height: 36px;
      padding: 0 10px;
      cursor: pointer;
      font-size: $--font-size-base;

      &:hover {
        color: $--dropdown-menuItem-hover-color;
      }

      @include when(active) {
        background-color: $--color-primary;
        color: $--color-white;
      }
    }

    @include e(content) {
      min-width: 100px;
    }

    @include e(bottom) {
      border-top: 1px solid $--border-color-lighter;
      padding: 8px;
    }

    @include e(wrap) {
      max-height: 280px;
    }

    @include e(checkbox-group) {
      padding: 10px;

      label.zm-checkbox {
        display: block;
        margin-right: 5px;
        margin-bottom: 8px;
        margin-left: 5px;
      }

      .zm-checkbox:last-child {
        margin-bottom: 0;
      }
    }
  }
</style>
